import type { CSSProperties } from 'react';
import type { BlockStyle } from './types';

// ---------------------------------------------------------------------------
// Background presets shared by the builder (BlockRenderer) and the public page
// (PublicCampaign). Keeping the layering math in ONE helper guarantees the
// canvas preview and the live page render byte-identically.
// ---------------------------------------------------------------------------

/** Ready-made gradient presets for the background (and hero) blocks. */
export const GRADIENT_PRESETS: string[] = [
  'linear-gradient(135deg,#667eea,#764ba2)',
  'linear-gradient(135deg,#2563eb,#7c3aed)',
  'linear-gradient(135deg,#0ea5e9,#22d3ee)',
  'linear-gradient(135deg,#06b6d4,#3b82f6)',
  'linear-gradient(135deg,#10b981,#059669)',
  'linear-gradient(135deg,#34d399,#14b8a6)',
  'linear-gradient(135deg,#f59e0b,#ef4444)',
  'linear-gradient(135deg,#fb7185,#f43f5e)',
  'linear-gradient(135deg,#ec4899,#8b5cf6)',
  'linear-gradient(135deg,#a78bfa,#6366f1)',
  'linear-gradient(135deg,#f97316,#fbbf24)',
  'linear-gradient(160deg,#fde68a,#fca5a5)',
  'linear-gradient(135deg,#0f172a,#334155)',
  'linear-gradient(135deg,#111827,#1f2937)',
  'linear-gradient(135deg,#1e3a8a,#0f766e)',
  'linear-gradient(135deg,#7c3aed,#db2777)',
  'radial-gradient(circle at 30% 20%,#60a5fa,#1e3a8a)',
  'radial-gradient(circle at 70% 30%,#f9a8d4,#7c3aed)',
  'linear-gradient(180deg,#e0f2fe,#fef3c7)',
  'linear-gradient(135deg,#fef9c3,#bbf7d0)',
];

// ---------------------------------------------------------------------------
// Decorative SVG patterns. Each generator returns a tile-able SVG as a CSS
// `url("data:image/svg+xml,...")` value, coloured + sized to the caller's
// taste. They layer ON TOP of the base colour / gradient.
// ---------------------------------------------------------------------------

// Ready-made photographic backgrounds (Unsplash CDN, verified reachable).
// `thumb` is a tiny variant for the picker grid; `url` is the full-bleed
// version applied to the block. All IDs are abstract/gradient/soft so they
// read well behind donation content.
const UNSPLASH = (id: string) => ({
  url: `https://images.unsplash.com/photo-${id}?w=1600&q=72&auto=format&fit=crop`,
  thumb: `https://images.unsplash.com/photo-${id}?w=160&q=50&auto=format&fit=crop`,
});
export const BG_IMAGE_PRESETS: { url: string; thumb: string }[] = [
  UNSPLASH('1557682250-33bd709cbe85'), // purple/blue abstract
  UNSPLASH('1579546929518-9e396f3cc809'), // gradient mesh
  UNSPLASH('1559827260-dc66d52bef19'), // green aurora
  UNSPLASH('1550859492-d5da9d8e45f3'), // teal gradient
  UNSPLASH('1462331940025-496dfbfc7564'), // blue lights
  UNSPLASH('1620121692029-d088224ddc74'), // pastel blobs
  UNSPLASH('1604871000636-074fa5117945'), // warm gradient
  UNSPLASH('1618005182384-a83a8bd57fbe'), // pink/blue smoke
  UNSPLASH('1531297484001-80022131f5a1'), // soft workspace blur
  UNSPLASH('1497436072909-60f360e1d4b1'), // forest light
  UNSPLASH('1470071459604-3b5ec3a7fe05'), // misty mountains
  UNSPLASH('1508739773434-c26b3d09e071'), // sky clouds
  UNSPLASH('1441974231531-c6227db76b6e'), // sunlit forest
];

const svgUrl = (w: number, h: number, inner: string): string => {
  const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="${w}" height="${h}" viewBox="0 0 ${w} ${h}">${inner}</svg>`;
  return `url("data:image/svg+xml,${encodeURIComponent(svg)}")`;
};

export interface PatternDef {
  id: string;
  label: string;
  /** Returns the CSS background-image url() for the tile. */
  url: (color: string, opacity: number) => string;
  /** Default tile size in px (background-size). */
  tile: number;
}

export const PATTERN_DEFS: PatternDef[] = [
  {
    id: 'dots', label: 'נקודות', tile: 22,
    url: (c, o) => svgUrl(22, 22, `<circle cx="3" cy="3" r="2.2" fill="${c}" fill-opacity="${o}"/>`),
  },
  {
    id: 'grid', label: 'רשת', tile: 28,
    url: (c, o) => svgUrl(28, 28, `<path d="M28 0H0V28" fill="none" stroke="${c}" stroke-opacity="${o}" stroke-width="1.4"/>`),
  },
  {
    id: 'diagonal', label: 'קווים', tile: 16,
    url: (c, o) => svgUrl(16, 16, `<path d="M-4 4L4 -4M0 16L16 0M12 20L20 12" stroke="${c}" stroke-opacity="${o}" stroke-width="2"/>`),
  },
  {
    id: 'crosshatch', label: 'הצלבה', tile: 18,
    url: (c, o) => svgUrl(18, 18, `<path d="M0 0L18 18M18 0L0 18" stroke="${c}" stroke-opacity="${o}" stroke-width="1.3"/>`),
  },
  {
    id: 'waves', label: 'גלים', tile: 40,
    url: (c, o) => svgUrl(40, 16, `<path d="M0 8 q10 -8 20 0 t20 0" fill="none" stroke="${c}" stroke-opacity="${o}" stroke-width="2"/>`),
  },
  {
    id: 'triangles', label: 'משולשים', tile: 30,
    url: (c, o) => svgUrl(30, 30, `<path d="M15 4L26 24H4Z" fill="${c}" fill-opacity="${o}"/>`),
  },
  {
    id: 'plus', label: 'פלוס', tile: 26,
    url: (c, o) => svgUrl(26, 26, `<path d="M13 6v14M6 13h14" stroke="${c}" stroke-opacity="${o}" stroke-width="2.4" stroke-linecap="round"/>`),
  },
  {
    id: 'rings', label: 'טבעות', tile: 32,
    url: (c, o) => svgUrl(32, 32, `<circle cx="16" cy="16" r="8" fill="none" stroke="${c}" stroke-opacity="${o}" stroke-width="2"/>`),
  },
  {
    id: 'chevron', label: 'זיגזג', tile: 28,
    url: (c, o) => svgUrl(28, 14, `<path d="M0 12L14 2L28 12" fill="none" stroke="${c}" stroke-opacity="${o}" stroke-width="2.2"/>`),
  },
  {
    id: 'confetti', label: 'קונפטי', tile: 44,
    url: (c, o) => svgUrl(44, 44, `<circle cx="6" cy="8" r="2.4" fill="${c}" fill-opacity="${o}"/><rect x="28" y="6" width="5" height="5" rx="1" fill="${c}" fill-opacity="${o}" transform="rotate(20 30 8)"/><path d="M20 30l3 3" stroke="${c}" stroke-opacity="${o}" stroke-width="2.4" stroke-linecap="round"/><circle cx="36" cy="32" r="2" fill="${c}" fill-opacity="${o}"/><rect x="6" y="30" width="5" height="5" rx="1" fill="${c}" fill-opacity="${o}" transform="rotate(-15 8 32)"/>`),
  },
  {
    id: 'hearts', label: 'לבבות', tile: 36,
    url: (c, o) => svgUrl(36, 36, `<path d="M18 14c-2-4-8-4-8 1 0 4 8 8 8 8s8-4 8-8c0-5-6-5-8-1z" fill="${c}" fill-opacity="${o}"/>`),
  },
  {
    id: 'bubbles', label: 'בועות', tile: 50,
    url: (c, o) => svgUrl(50, 50, `<circle cx="12" cy="14" r="6" fill="${c}" fill-opacity="${o}"/><circle cx="36" cy="30" r="9" fill="${c}" fill-opacity="${o}"/><circle cx="42" cy="8" r="3" fill="${c}" fill-opacity="${o}"/>`),
  },
];

export const getPattern = (id?: string): PatternDef | undefined =>
  id ? PATTERN_DEFS.find(p => p.id === id) : undefined;

/**
 * Computes the layered background CSS for a `background` block, merging
 * (top → bottom): decorative pattern → photo OR gradient → solid colour.
 * Used by BOTH BlockRenderer and PublicCampaign so they match 1:1.
 */
export function backgroundLayerStyle(
  s: BlockStyle,
  p: Record<string, any>,
): CSSProperties {
  if (s.bgTransparent) {
    return { backgroundColor: 'transparent', backgroundImage: 'none' };
  }
  const photo: string | undefined = p?.imageUrl || p?.bgImage;
  const pattern = getPattern(p?.bgPattern);

  const images: string[] = [];
  const sizes: string[] = [];
  const repeats: string[] = [];
  const positions: string[] = [];

  if (pattern) {
    const opacity = Math.max(0, Math.min(100, p?.bgPatternOpacity ?? 100)) / 100;
    const tile = p?.bgPatternScale || pattern.tile;
    images.push(pattern.url(p?.bgPatternColor || '#94a3b8', opacity));
    sizes.push(`${tile}px ${tile}px`);
    repeats.push('repeat');
    positions.push('0 0');
  }
  if (photo) {
    images.push(`url("${photo}")`);
    sizes.push('cover');
    repeats.push('no-repeat');
    positions.push('center');
  } else if (s.bgGradient) {
    images.push(s.bgGradient);
    sizes.push('cover');
    repeats.push('no-repeat');
    positions.push('center');
  }

  return {
    backgroundColor: s.bgColor || undefined,
    backgroundImage: images.length ? images.join(', ') : undefined,
    backgroundSize: images.length ? sizes.join(', ') : undefined,
    backgroundRepeat: images.length ? repeats.join(', ') : undefined,
    backgroundPosition: images.length ? positions.join(', ') : undefined,
  };
}
